import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Heading } from '@vwfs-bronson/bronson-react';
import {
  AuthConfig,
  AuthTokens,
  clearAuthSession,
  exchangeCodeForTokens,
  getStoredTokens,
  redirectToLogin,
  redirectToLogout,
} from './auth';
import {
  AgentListResponse,
  AgentTemplateListResponse,
  EnvironmentListResponse,
  FesConfig,
  JobListResponse,
  SkillListResponse,
  Team,
  TeamListResponse,
  UnclaimedInstallationListResponse,
  getAgents,
  getEnvironments,
  getJobs,
  getSkills,
  getTeams,
  getTemplates,
  getUnclaimedInstallations,
  syncAuthenticatedUser,
} from './fes';
import { ConfigurationView, getNavGroupLabel, navGroups } from './views';
import { AgentsTable } from './components/AgentsTable';
import { EnvironmentsTable } from './components/EnvironmentsTable';
import { GitHubAppsTable } from './components/GitHubAppsTable';
import { SkillsTable } from './components/SkillsTable';
import { TableLoading } from './components/TableLoading';
import { TemplatesTable } from './components/TemplatesTable';
import { JobsView } from './components/jobs/JobsView';
import { TeamsView } from './components/teams/TeamsView';
import './App.css';

type ViewState =
  | { status: 'loading'; message: string }
  | { status: 'signedOut' }
  | { status: 'callback'; message: string }
  | {
      status: 'ready';
      tokens: AuthTokens;
      activeView: ConfigurationView;
      teams?: TeamListResponse;
      agents?: AgentListResponse;
      templates?: AgentTemplateListResponse;
      skills?: SkillListResponse;
      environments?: EnvironmentListResponse;
      unclaimedInstallations?: UnclaimedInstallationListResponse;
      jobs?: JobListResponse;
      loadingView?: ConfigurationView;
    }
  | { status: 'error'; message: string };

let callbackExchangePromise: Promise<AuthTokens> | null = null;

function App() {
  const stage = process.env.REACT_APP_STAGE || 'local';
  const authConfig = useMemo<AuthConfig>(
    () => ({
      cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN || '',
      clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
      redirectUri: process.env.REACT_APP_COGNITO_REDIRECT_URI || '',
      logoutUri: process.env.REACT_APP_COGNITO_LOGOUT_URI || '',
      scopes: ['openid', 'email', 'profile'],
    }),
    []
  );
  const fesConfig = useMemo<FesConfig>(
    () => ({
      baseUrl: process.env.REACT_APP_FES_BASE_URL || '',
    }),
    []
  );
  const [view, setView] = useState<ViewState>({ status: 'loading', message: 'Checking authentication session...' });

  const loadHome = useCallback(
    async (tokens: AuthTokens) => {
      void syncAuthenticatedUser(fesConfig, tokens).catch(error => {
        console.error('FES auth sync failed.', error);
      });

      setView({ status: 'loading', message: 'Loading teams from FES...' });
      const teams = await getTeams(fesConfig, tokens);

      setView({ status: 'ready', tokens, activeView: 'Teams', teams });
    },
    [fesConfig]
  );

  const handleNavigation = (item: ConfigurationView) => {
    setView(currentView => {
      if (currentView.status !== 'ready') {
        return currentView;
      }

      if (item === currentView.activeView) {
        return currentView;
      }

      if (item === 'Teams' && currentView.teams) {
        return { ...currentView, activeView: 'Teams' };
      }

      if (item === 'Agents' && currentView.agents) {
        return { ...currentView, activeView: 'Agents' };
      }

      if (item === 'Templates' && currentView.templates) {
        return { ...currentView, activeView: 'Templates' };
      }

      if (item === 'Skills' && currentView.skills) {
        return { ...currentView, activeView: 'Skills' };
      }

      if (item === 'Environments' && currentView.environments) {
        return { ...currentView, activeView: 'Environments' };
      }

      if (item === 'GitHub Apps' && currentView.unclaimedInstallations) {
        return { ...currentView, activeView: 'GitHub Apps' };
      }

      if (item === 'Jobs' && currentView.jobs) {
        return { ...currentView, activeView: 'Jobs' };
      }

      return { ...currentView, activeView: item, loadingView: item };
    });

    const currentView = view;

    if (currentView.status !== 'ready') {
      return;
    }

    if (item === 'Teams' && currentView.teams) {
      return;
    }

    if (item === 'Agents' && currentView.agents) {
      return;
    }

    if (item === 'Templates' && currentView.templates) {
      return;
    }

    if (item === 'Skills' && currentView.skills) {
      return;
    }

    if (item === 'Environments' && currentView.environments) {
      return;
    }

    if (item === 'GitHub Apps' && currentView.unclaimedInstallations) {
      return;
    }

    if (item === 'Jobs' && currentView.jobs) {
      return;
    }

    const load = async () => {
      try {
        if (item === 'Teams') {
          const teams = await getTeams(fesConfig, currentView.tokens);
          setView(latestView =>
            latestView.status === 'ready'
              ? { ...latestView, teams, loadingView: latestView.loadingView === 'Teams' ? undefined : latestView.loadingView }
              : latestView
          );
          return;
        }

        if (item === 'Agents') {
          const agents = await getAgents(fesConfig, currentView.tokens);
          setView(latestView =>
            latestView.status === 'ready'
              ? { ...latestView, agents, loadingView: latestView.loadingView === 'Agents' ? undefined : latestView.loadingView }
              : latestView
          );
          return;
        }

        if (item === 'Skills') {
          const skills = await getSkills(fesConfig, currentView.tokens);
          setView(latestView =>
            latestView.status === 'ready'
              ? { ...latestView, skills, loadingView: latestView.loadingView === 'Skills' ? undefined : latestView.loadingView }
              : latestView
          );
          return;
        }

        if (item === 'Environments') {
          const environments = await getEnvironments(fesConfig, currentView.tokens);
          setView(latestView =>
            latestView.status === 'ready'
              ? {
                  ...latestView,
                  environments,
                  loadingView: latestView.loadingView === 'Environments' ? undefined : latestView.loadingView,
                }
              : latestView
          );
          return;
        }

        if (item === 'GitHub Apps') {
          const unclaimedInstallations = await getUnclaimedInstallations(fesConfig, currentView.tokens);
          setView(latestView =>
            latestView.status === 'ready'
              ? {
                  ...latestView,
                  unclaimedInstallations,
                  loadingView: latestView.loadingView === 'GitHub Apps' ? undefined : latestView.loadingView,
                }
              : latestView
          );
          return;
        }

        if (item === 'Jobs') {
          const jobs = await getJobs(fesConfig, currentView.tokens);
          setView(latestView =>
            latestView.status === 'ready'
              ? { ...latestView, jobs, loadingView: latestView.loadingView === 'Jobs' ? undefined : latestView.loadingView }
              : latestView
          );
          return;
        }

        const templates = await getTemplates(fesConfig, currentView.tokens);
        setView(latestView =>
          latestView.status === 'ready'
            ? { ...latestView, templates, loadingView: latestView.loadingView === 'Templates' ? undefined : latestView.loadingView }
            : latestView
        );
      } catch (error) {
        setView({ status: 'error', message: error instanceof Error ? error.message : `Unable to load ${item}.` });
      }
    };

    void load();
  };

  const handleJobsPageChange = async (offset: number) => {
    if (view.status !== 'ready') {
      return;
    }

    try {
      const jobs = await getJobs(fesConfig, view.tokens, { offset });
      setView(latestView => (latestView.status === 'ready' ? { ...latestView, jobs } : latestView));
    } catch (error) {
      setView({ status: 'error', message: error instanceof Error ? error.message : 'Unable to load jobs.' });
    }
  };

  const handleTeamSaved = (savedTeam: Team) => {
    setView(latestView => {
      if (latestView.status !== 'ready' || !latestView.teams) {
        return latestView;
      }

      const currentTeams = latestView.teams.data || [];
      const savedTeamExists = currentTeams.some(team => team.teamId === savedTeam.teamId);

      return {
        ...latestView,
        teams: {
          ...latestView.teams,
          data: savedTeamExists
            ? currentTeams.map(team => (team.teamId === savedTeam.teamId ? { ...team, ...savedTeam } : team))
            : [savedTeam, ...currentTeams],
          pagination: latestView.teams.pagination
            ? {
                ...latestView.teams.pagination,
                total:
                  typeof latestView.teams.pagination.total === 'number' && !savedTeamExists
                    ? latestView.teams.pagination.total + 1
                    : latestView.teams.pagination.total,
              }
            : latestView.teams.pagination,
        },
      };
    });
  };

  useEffect(() => {
    const initialize = async () => {
      const params = new URLSearchParams(window.location.search);
      const authError = params.get('error');
      const code = params.get('code');
      const state = params.get('state');

      if (authError) {
        setView({
          status: 'error',
          message: `${authError}: ${params.get('error_description') || 'Cognito returned an authentication error.'}`,
        });
        return;
      }

      if (code && state) {
        setView({ status: 'callback', message: 'Completing Cognito login...' });

        try {
          if (!callbackExchangePromise) {
            callbackExchangePromise = exchangeCodeForTokens(authConfig, code, state);
          }

          const tokens = await callbackExchangePromise;
          window.history.replaceState({}, document.title, '/');
          await loadHome(tokens);
        } catch (error) {
          callbackExchangePromise = null;
          clearAuthSession();
          setView({ status: 'error', message: error instanceof Error ? error.message : 'Cognito login failed.' });
        }

        return;
      }

      const tokens = getStoredTokens();

      if (!tokens) {
        setView({ status: 'signedOut' });
        return;
      }

      try {
        await loadHome(tokens);
      } catch (error) {
        clearAuthSession();
        setView({ status: 'error', message: error instanceof Error ? error.message : 'FES request failed.' });
      }
    };

    void initialize();
  }, [authConfig, loadHome]);

  const handleLogin = () => {
    redirectToLogin(authConfig).catch(error => {
      setView({ status: 'error', message: error instanceof Error ? error.message : 'Unable to start Cognito login.' });
    });
  };

  if (view.status === 'ready') {
    const teams = view.teams?.data || [];
    const totalTeams = view.teams?.pagination?.total ?? teams.length;
    const agents = view.agents?.data || [];
    const totalAgents = view.agents?.pagination?.total ?? agents.length;
    const templates = view.templates?.data || [];
    const totalTemplates = view.templates?.pagination?.total ?? templates.length;
    const skills = view.skills?.data || [];
    const totalSkills = view.skills?.pagination?.total ?? skills.length;
    const environments = view.environments?.data || [];
    const totalEnvironments = environments.length;
    const unclaimedInstallations = view.unclaimedInstallations?.data || [];
    const totalUnclaimedInstallations = view.unclaimedInstallations?.pagination?.total ?? unclaimedInstallations.length;
    const jobs = view.jobs?.data || [];
    const totalJobs = view.jobs?.pagination?.total ?? jobs.length;
    const isLoadingActiveView = view.loadingView === view.activeView;
    const activeTotalLabels: Partial<Record<ConfigurationView, string>> = {
      Agents: `${totalAgents} ${totalAgents === 1 ? 'agent' : 'agents'}`,
      Environments: `${totalEnvironments} ${totalEnvironments === 1 ? 'environment' : 'environments'}`,
      'GitHub Apps': `${totalUnclaimedInstallations} ${
        totalUnclaimedInstallations === 1 ? 'unclaimed installation' : 'unclaimed installations'
      }`,
      Skills: `${totalSkills} ${totalSkills === 1 ? 'skill' : 'skills'}`,
      Teams: `${totalTeams} ${totalTeams === 1 ? 'team' : 'teams'}`,
      Templates: `${totalTemplates} ${totalTemplates === 1 ? 'template' : 'templates'}`,
      Jobs: `${totalJobs} ${totalJobs === 1 ? 'job' : 'jobs'}`,
    };
    const activeGroupLabel = getNavGroupLabel(view.activeView);

    return (
      <main className="app-dashboard">
        <aside className="app-sidebar" aria-label="Primary navigation">
          <div className="app-brand">Agent Backoffice</div>
          <nav className="app-nav" aria-label="Configuration navigation">
            {navGroups.map(group => (
              <div key={group.label}>
                <div className="app-nav-group">{group.label}</div>
                <ul className="app-nav-list">
                  {group.items.map(item => (
                    <li key={item}>
                      <button
                        className={item === view.activeView ? 'app-nav-link app-nav-link--active' : 'app-nav-link'}
                        type="button"
                        onClick={() => handleNavigation(item)}
                      >
                        {item}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </aside>
        <section className="app-content" aria-labelledby="configuration-heading">
          <header className="app-content-header">
            <div>
              <div className="app-kicker">{activeGroupLabel}</div>
              <Heading level={1} id="configuration-heading" className="app-title">
                {view.activeView}
              </Heading>
              <p className="app-summary">{isLoadingActiveView ? 'Loading...' : activeTotalLabels[view.activeView]}</p>
            </div>
            <div className="app-actions">
              <Button secondary title={`Current stage: ${stage}`}>
                Stage: {stage}
              </Button>
              <Button onClick={() => redirectToLogout(authConfig)}>Logout</Button>
            </div>
          </header>
          {isLoadingActiveView ? <TableLoading view={view.activeView} /> : null}
          {!isLoadingActiveView && view.activeView === 'Templates' ? <TemplatesTable templates={templates} /> : null}
          {!isLoadingActiveView && view.activeView === 'Skills' ? <SkillsTable skills={skills} /> : null}
          {!isLoadingActiveView && view.activeView === 'Teams' ? (
            <TeamsView fesConfig={fesConfig} tokens={view.tokens} teams={teams} onTeamSaved={handleTeamSaved} />
          ) : null}
          {!isLoadingActiveView && view.activeView === 'Agents' ? <AgentsTable agents={agents} /> : null}
          {!isLoadingActiveView && view.activeView === 'GitHub Apps' ? (
            <GitHubAppsTable installations={unclaimedInstallations} />
          ) : null}
          {!isLoadingActiveView && view.activeView === 'Environments' ? <EnvironmentsTable environments={environments} /> : null}
          {!isLoadingActiveView && view.activeView === 'Jobs' ? (
            <JobsView
              fesConfig={fesConfig}
              tokens={view.tokens}
              jobs={jobs}
              pagination={view.jobs?.pagination}
              onPageChange={handleJobsPageChange}
            />
          ) : null}
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <section className="app-hero" aria-labelledby="home-heading">
        <div className="app-kicker">Agent Backoffice</div>
        <Heading level={1} id="home-heading" className="app-title">
          Agent Backoffice
        </Heading>
        {view.status === 'signedOut' && <p className="app-greeting">Sign in with Cognito to load teams from FES.</p>}
        {(view.status === 'loading' || view.status === 'callback') && <p className="app-greeting">{view.message}</p>}
        {view.status === 'error' && (
          <div className="app-result" role="alert">
            {view.message}
          </div>
        )}
        <div className="app-actions">
          <Button secondary title={`Current stage: ${stage}`}>
            Stage: {stage}
          </Button>
          {view.status === 'signedOut' || view.status === 'error' ? <Button onClick={handleLogin}>Login</Button> : null}
        </div>
      </section>
    </main>
  );
}

export default App;
